import { useMemo } from 'react';

import {
  Box,
  Button as MuiButton,
  Card,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

import ClaimFactList, { type FactGroup } from '@/james-playground/aa-family-claim/ClaimFactList';
import ClaimTheme from '@/james-playground/aa-family-claim/ClaimTheme';
import { AREAS, OWNER, say } from '@/james-playground/aa-family-claim/claim/asks';
import { voiceOf, type ClaimState } from '@/james-playground/aa-family-claim/claim/claim';
import { ADVISER } from '@/james-playground/aa-family-claim/claim/claim.content';
import { pathwayOf, sendBy, skipped, statusOf } from '@/james-playground/aa-family-claim/claim/engine';
import { labelOf, valueText } from '@/james-playground/aa-family-claim/claim/labels';
import { ENTRIES, isAsked } from '@/james-playground/aa-family-claim/claim/registry';
import { SCENARIO } from '@/james-playground/aa-family-claim/claim/scenarios';

const Tile = ({ value, label }: { value: string; label: string }) => (
  <Card sx={{ p: 2, flex: 1, minWidth: 160 }}>
    <Stack spacing={0.5}>
      <Typography variant='h3'>{value}</Typography>
      <Typography variant='body2'>{label}</Typography>
    </Stack>
  </Card>
);

/**
 * The claim as the Care Adviser receives it (spec 12): the pathway flags, the
 * start-date deadline, everything the family set aside to talk through, and
 * every answer with where it came from.
 */
const AdviserView = ({ claim }: { claim: ClaimState }) => {
  const scenario = SCENARIO[claim.scenario];
  const v = voiceOf(claim);
  const { facts } = claim;
  const from = scenario.helper?.first ?? scenario.claimant.first;
  const path = pathwayOf(facts, scenario.selfClaim);
  const aside = skipped(facts);
  const by = sendBy(facts);

  const groups: FactGroup[] = useMemo(
    () =>
      AREAS.map((area) => ({
        label: say(area.label, v),
        rows: ENTRIES.filter(
          (e) => isAsked(e) && OWNER[e.id]?.area === area.id && statusOf(e, facts) === 'confirmed',
        ).map((e) => ({
          id: e.id,
          label: labelOf(e.id, v),
          value: valueText(e.id, facts[e.id]?.value),
          note: e.q ? `On the form: ${e.q.split('.')[0]}` : undefined,
          source: { label: ADVISER.source(facts[e.id].source, from), quote: facts[e.id].quote },
        })),
      })).filter((g) => g.rows.length),
    [facts, from, v],
  );

  const flags = [
    path.specialRules && ADVISER.flags.quicker,
    path.inCare && ADVISER.flags.inCare(path.placeType),
    path.inCare && path.publiclyFunded && ADVISER.flags.funded,
    path.selfClaim && ADVISER.flags.herself,
    path.signer === 'attorney' && ADVISER.flags.attorney,
    path.signer === 'applying' && ADVISER.flags.applying,
    path.signer === 'deputy' && ADVISER.flags.deputy,
    path.signer === 'appointee' && ADVISER.flags.appointee,
    path.signer === 'organisation' && ADVISER.flags.organisation,
    path.signer === 'unsure' && !path.selfClaim && ADVISER.flags.unsure,
  ].filter((f): f is string => Boolean(f));

  const answered = ENTRIES.filter((e) => isAsked(e) && facts[e.id]?.state === 'confirmed').length;

  return (
    <ClaimTheme>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between', px: 4, minHeight: 60, flexShrink: 0, borderBottom: 1, borderColor: 'custom.neutral.200' }}
          direction='row'
        >
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
            <Box sx={{ width: 30, height: 'auto' }} component='img' src='/vector/karehero-logo.png' alt='KareHero' />
            <Typography variant='h6'>Care Adviser</Typography>
          </Stack>
          <Typography variant='body2'>{ADVISER.signedIn}</Typography>
        </Stack>

        <Stack sx={{ flex: 1, minHeight: 0 }} direction='row'>
          <Box component='nav' aria-label={ADVISER.inbox} sx={{ width: 280, flexShrink: 0, borderRight: 1, borderColor: 'custom.neutral.200', p: 2, overflow: 'auto' }}>
            <Typography variant='overline'>{ADVISER.inbox}</Typography>
            <Card sx={{ mt: 1, p: 2, borderColor: 'primary.main', bgcolor: 'custom.limeGreen.100' }}>
              <Stack spacing={0.5}>
                <Stack sx={{ alignItems: 'center', justifyContent: 'space-between' }} direction='row'>
                  <Typography variant='h6'>{scenario.claimant.full}</Typography>
                  <Chip size='small' color='primary' label='New' />
                </Stack>
                <Typography variant='body2'>Attendance Allowance</Typography>
                {/* Full strength: secondary grey on the selected card's green measured 4.45:1. */}
                <Typography sx={{ color: 'text.primary' }} variant='caption'>
                  {scenario.helper
                    ? ADVISER.received(scenario.helper.full, scenario.helper.relationship)
                    : ADVISER.receivedSelf}
                </Typography>
              </Stack>
            </Card>
          </Box>

          <Box component='main' aria-label={ADVISER.title(scenario.claimant.full)} tabIndex={0} sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: 4, '&:focus-visible': { outline: 'none' } }}>
            <Stack spacing={3}>
              <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }} direction='row'>
                <Stack spacing={1}>
                  <Typography component='h1' variant='h2'>
                    {ADVISER.title(scenario.claimant.full)}
                  </Typography>
                  {flags.length > 0 && (
                    <Stack sx={{ flexWrap: 'wrap', gap: 1 }} direction='row'>
                      {flags.map((f) => (
                        <Chip key={f} size='small' variant='outlined' label={f} />
                      ))}
                    </Stack>
                  )}
                </Stack>
                <Stack direction='row' spacing={1.5}>
                  <MuiButton variant='outlined'>{ADVISER.call(from)}</MuiButton>
                  <MuiButton variant='outlined'>{ADVISER.download}</MuiButton>
                  <MuiButton variant='contained'>{ADVISER.reviewed}</MuiButton>
                </Stack>
              </Stack>

              <Stack sx={{ flexWrap: 'wrap', gap: 2 }} direction='row'>
                <Tile value={`${answered}`} label={ADVISER.tiles.answered} />
                <Tile value={`${aside.length}`} label={ADVISER.tiles.talkThrough(aside.length)} />
                {by && (
                  <Tile
                    value={by.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    label={ADVISER.tiles.deadline}
                  />
                )}
              </Stack>

              <Stack sx={{ alignItems: 'flex-start', gap: 3 }} direction={{ xs: 'column', lg: 'row' }}>
                <Stack sx={{ flex: 2, minWidth: 0, width: '100%' }} spacing={1.5}>
                  <Typography variant='h4'>{ADVISER.answersLabel}</Typography>
                  <ClaimFactList groups={groups} collapsible save='explicit' />
                </Stack>
                <Stack sx={{ flex: 1, minWidth: 280, width: '100%' }} spacing={1}>
                  <Typography variant='h4'>{ADVISER.talkThroughLabel}</Typography>
                  <Card sx={{ p: 0, overflow: 'hidden' }}>
                    {aside.length ? (
                      aside.map((e, i) => (
                        <Stack key={e.id} sx={{ px: 2, py: 1.25, borderTop: i === 0 ? 0 : 1, borderColor: 'custom.neutral.200' }} spacing={0.25}>
                          <Typography variant='body2'>{labelOf(e.id, v)}</Typography>
                          <Typography sx={{ color: 'text.secondary' }} variant='caption'>
                            {say(AREAS.find((a) => a.id === OWNER[e.id]?.area)?.label ?? '', v)}
                            {e.q ? ` · ${e.q.split('.')[0]}` : ''}
                          </Typography>
                        </Stack>
                      ))
                    ) : (
                      <Typography sx={{ p: 2 }} variant='body2'>
                        {ADVISER.talkThroughNone}
                      </Typography>
                    )}
                  </Card>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </ClaimTheme>
  );
};

export default AdviserView;
